/** Невидимые символы, которые приходят со вставкой из мессенджеров и документов. */
const INVISIBLE = /[\u00a0\u200b-\u200d\u2060\ufeff]/g;

/** Адрес для хранения и сравнения: без пробелов и невидимых символов, в нижнем регистре. */
export function normalizeEmail(value: string): string {
  return value.replace(INVISIBLE, '').trim().toLowerCase();
}

// Домен из меток букв и цифр (включая кириллицу — адреса EAI), зона — от двух букв или punycode.
const EMAIL = /^[^\s@]+@(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+(?:\p{L}{2,}|xn--[a-z0-9-]+)$/u;

export function isEmail(value: string): boolean {
  return EMAIL.test(value);
}

/** Популярные почтовые домены, от частых к редким: при равенстве расстояний выигрывает первый. */
const KNOWN_DOMAINS = [
  'gmail.com',
  'yandex.ru',
  'mail.ru',
  'ya.ru',
  'bk.ru',
  'inbox.ru',
  'list.ru',
  'rambler.ru',
  'icloud.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'me.com',
  'mail.com',
  'yandex.com',
  'internet.ru',
  'proton.me',
  'protonmail.com',
  'live.com',
];

/** Расстояние Дамерау — Левенштейна (перестановка соседних букв — одна правка). */
function distance(a: string, b: string): number {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d: number[][] = Array.from({ length: rows }, (_, i) =>
    Array.from({ length: cols }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  const at = (i: number, j: number) => d[i]?.[j] ?? Number.POSITIVE_INFINITY;
  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min(at(i - 1, j) + 1, at(i, j - 1) + 1, at(i - 1, j - 1) + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        best = Math.min(best, at(i - 2, j - 2) + 1);
      }
      const row = d[i];
      if (row) row[j] = best;
    }
  }
  return at(a.length, b.length);
}

/**
 * Подсказка «Может быть, anna@gmail.com?» для опечатки в популярном домене.
 * Короткие домены (до 5 символов) исправляются только на одну правку, чтобы не гадать.
 */
export function suggestEmail(value: string): string | null {
  const email = normalizeEmail(value);
  const at = email.lastIndexOf('@');
  if (at <= 0 || email.indexOf('@') !== at) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (domain === '' || KNOWN_DOMAINS.includes(domain)) return null;

  let best: { domain: string; distance: number } | null = null;
  for (const known of KNOWN_DOMAINS) {
    const limit = known.length > 5 ? 2 : 1;
    const d = distance(domain, known);
    if (d <= limit && (best === null || d < best.distance)) best = { domain: known, distance: d };
  }
  return best ? `${local}@${best.domain}` : null;
}
